import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

async function call(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Terjadi kesalahan");
  return data;
}

const inputCls =
  "h-10 w-full rounded-lg border border-nm-border bg-nm-bg2 px-3 text-sm text-nm-text focus:border-nm-red focus:outline-none focus:ring-2 focus:ring-nm-red/30";
const btn =
  "inline-flex h-10 items-center justify-center rounded-lg bg-nm-red px-5 text-sm font-semibold text-white transition-colors hover:bg-nm-red-hover disabled:opacity-50";

export default function AdminPage() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setMe(await call("/me"));
    } catch {
      setMe(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (checking)
    return <Shell><p className="text-nm-muted">Memuat...</p></Shell>;
  if (!me) return <Login onDone={refresh} />;
  if (me.must_change_password) return <ChangePassword forced onDone={refresh} />;
  return <Dashboard me={me} onLogout={refresh} />;
}

function Shell({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-nm-bg px-4">
      <div className="w-full max-w-md rounded-2xl border border-nm-border bg-nm-surface p-6">
        {children}
      </div>
    </div>
  );
}

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgot, setForgot] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (forgot) {
        const r = await call("/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
        toast.success(r.email_configured
          ? "Tautan reset dikirim ke email admin."
          : "Pengiriman email belum dikonfigurasi. Silakan pulihkan kata sandi lewat metode pemulihan yang telah dikonfigurasi (sisi server).");
        setForgot(false);
      } else {
        await call("/login", { method: "POST", body: JSON.stringify({ email, password }) });
        onDone();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="text-xl font-extrabold text-nm-text">Admin Nofina Movie</h1>
      <p className="mt-1 text-sm text-nm-muted">
        {forgot ? "Masukkan email untuk tautan reset." : "Masuk untuk mengelola katalog."}
      </p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <input data-testid="admin-email" className={inputCls} type="email" placeholder="Email"
          value={email} onChange={(e) => setEmail(e.target.value)} required />
        {!forgot && (
          <input data-testid="admin-password" className={inputCls} type="password" placeholder="Kata sandi"
            value={password} onChange={(e) => setPassword(e.target.value)} required />
        )}
        <button data-testid="admin-login-submit" className={`${btn} w-full`} disabled={busy}>
          {forgot ? "Kirim Tautan Reset" : "Masuk"}
        </button>
      </form>
      <button onClick={() => setForgot(!forgot)}
        className="mt-4 text-xs font-semibold text-nm-red hover:underline">
        {forgot ? "Kembali ke masuk" : "Lupa kata sandi?"}
      </button>
    </Shell>
  );
}

function ChangePassword({ forced, onDone, onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await call("/change-password", {
        method: "POST",
        body: JSON.stringify(forced ? { new_password: next } : { current_password: cur, new_password: next }),
      });
      toast.success("Kata sandi diperbarui.");
      onDone ? onDone() : onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell>
      <h1 className="text-xl font-extrabold text-nm-text">
        {forced ? "Buat Kata Sandi Baru" : "Ubah Kata Sandi"}
      </h1>
      {forced && <p className="mt-1 text-sm text-nm-muted">Wajib diganti sebelum lanjut.</p>}
      <form onSubmit={submit} className="mt-5 space-y-3">
        {!forced && (
          <input className={inputCls} type="password" placeholder="Kata sandi saat ini"
            value={cur} onChange={(e) => setCur(e.target.value)} required />
        )}
        <input data-testid="admin-new-password" className={inputCls} type="password"
          placeholder="Kata sandi baru (min 6)" value={next} onChange={(e) => setNext(e.target.value)} required />
        <button data-testid="admin-change-submit" className={`${btn} w-full`} disabled={busy}>Simpan</button>
      </form>
      {!forced && (
        <button onClick={onClose} className="mt-4 text-xs font-semibold text-nm-muted hover:underline">Batal</button>
      )}
    </Shell>
  );
}

function Dashboard({ me, onLogout }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [showPw, setShowPw] = useState(false);

  const load = useCallback(async () => {
    setProducts(await call("/products"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    await call("/logout", { method: "POST" }).catch(() => {});
    onLogout();
  };

  const toggle = async (slug) => {
    await call(`/products/${slug}/visibility`, { method: "PATCH" });
    toast.success("Status diperbarui.");
    load();
  };

  const filtered = products.filter((p) => {
    const s = q.trim().toLowerCase();
    return !s || p.name.toLowerCase().includes(s) ||
      p.variants.some((v) => v.code.toLowerCase().includes(s));
  });

  if (showPw) return <ChangePassword onClose={() => setShowPw(false)} onDone={() => setShowPw(false)} />;
  if (editing !== null)
    return <ProductForm product={editing} onDone={() => { setEditing(null); load(); }} />;

  return (
    <div className="min-h-screen bg-nm-bg text-nm-text">
      <header className="flex items-center justify-between border-b border-nm-border px-4 py-4 sm:px-6">
        <div>
          <h1 className="text-lg font-extrabold">Katalog Admin</h1>
          <p className="text-xs text-nm-muted">{me.email}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowPw(true)} className="rounded-lg border border-nm-border px-3 py-2 text-xs font-semibold hover:bg-nm-surface2">Ubah Sandi</button>
          <button data-testid="admin-logout" onClick={logout} className="rounded-lg border border-nm-border px-3 py-2 text-xs font-semibold hover:bg-nm-surface2">Keluar</button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input data-testid="admin-search" className={`${inputCls} sm:max-w-xs`} placeholder="Cari nama atau kode produk"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button data-testid="admin-add-product" className={btn}
            onClick={() => setEditing({ slug: "", name: "", category: "", description: "", isVisible: true, isFeatured: false, sortOrder: 999, variants: [] })}>
            + Tambah Produk
          </button>
        </div>
        <p className="mt-3 text-sm text-nm-muted">{filtered.length} produk</p>

        <div className="mt-3 space-y-2">
          {filtered.map((p) => (
            <div key={p.slug} data-testid={`admin-product-${p.slug}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-nm-border bg-nm-surface p-3">
              <div className="flex items-center gap-3 min-w-0">
                {p.iconUrl ? <img src={p.iconUrl} alt="" className="h-9 w-9 rounded-md bg-white/90 object-contain p-1" /> : null}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{p.name}</p>
                  <p className="text-xs text-nm-muted">{p.variants.length} varian · {p.isVisible ? "Aktif" : "Nonaktif"}{p.isFeatured ? " · Unggulan" : ""}</p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => toggle(p.slug)} data-testid={`admin-toggle-${p.slug}`}
                  className="rounded-lg border border-nm-border px-3 py-1.5 text-xs font-semibold hover:bg-nm-surface2">
                  {p.isVisible ? "Nonaktifkan" : "Aktifkan"}
                </button>
                <button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.slug}`}
                  className="rounded-lg bg-nm-red px-3 py-1.5 text-xs font-semibold text-white hover:bg-nm-red-hover">Edit</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductForm({ product, onDone }) {
  const isNew = !product.slug || !product.name;
  const [f, setF] = useState({ ...product, variants: product.variants.map((v) => ({ ...v })) });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setV = (i, k, v) => setF((s) => {
    const variants = s.variants.map((x, j) => (j === i ? { ...x, [k]: v } : x));
    return { ...s, variants };
  });
  const addV = () => setF((s) => ({ ...s, variants: [...s.variants, { code: "", durationLabel: "", price: 0, stock: 1, isActive: true }] }));

  const uploadIcon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Format harus PNG, JPG, atau WebP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 2MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/upload-icon`, { method: "POST", credentials: "include", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Gagal mengunggah");
      set("iconUrl", data.iconUrl);
      toast.success("Ikon diunggah.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const iconSrc = !f.iconUrl
    ? ""
    : f.iconUrl.startsWith("http")
      ? f.iconUrl
      : f.iconUrl.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL}${f.iconUrl}`
        : f.iconUrl;

  const save = async (e) => {
    e.preventDefault();
    if (!window.confirm("Simpan perubahan produk ini?")) return;
    setBusy(true);
    try {
      const body = {
        ...f,
        slug: (f.slug || f.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
        sortOrder: Number(f.sortOrder) || 999,
        variants: f.variants.map((v) => ({
          code: v.code.trim(), durationLabel: v.durationLabel,
          price: parseInt(v.price, 10), stock: parseInt(v.stock, 10) || 0, isActive: v.isActive !== false,
        })),
      };
      if (isNew) await call("/products", { method: "POST", body: JSON.stringify(body) });
      else await call(`/products/${product.slug}`, { method: "PUT", body: JSON.stringify(body) });
      toast.success("Produk disimpan.");
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nm-bg text-nm-text">
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <button onClick={onDone} className="mb-4 text-sm font-semibold text-nm-muted hover:text-nm-text">← Kembali</button>
        <h1 className="text-lg font-extrabold">{isNew ? "Tambah Produk" : `Edit ${product.name}`}</h1>
        <form onSubmit={save} className="mt-5 space-y-3">
          <input className={inputCls} placeholder="Nama produk" data-testid="pf-name"
            value={f.name} onChange={(e) => set("name", e.target.value)} required />

          <div className="flex items-center gap-4 rounded-xl border border-nm-border p-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-nm-border bg-nm-bg2">
              {iconSrc
                ? <img src={iconSrc} alt="" data-testid="pf-icon-preview" className="h-full w-full object-contain p-1" />
                : <span className="text-[10px] text-nm-muted text-center px-1">Tidak ada ikon</span>}
            </div>
            <div className="min-w-0">
              <label className="inline-flex h-9 cursor-pointer items-center justify-center rounded-lg border border-nm-border px-4 text-xs font-semibold hover:bg-nm-surface2">
                <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="pf-icon-input"
                  className="hidden" onChange={uploadIcon} disabled={uploading} />
                {uploading ? "Mengunggah..." : (iconSrc ? "Ganti Ikon" : "Unggah Ikon")}
              </label>
              <p className="mt-1 text-[11px] text-nm-muted">PNG, JPG, atau WebP · maks 2MB</p>
            </div>
          </div>

          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.isVisible} onChange={(e) => set("isVisible", e.target.checked)} /> Aktif</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.isFeatured} onChange={(e) => set("isFeatured", e.target.checked)} /> Unggulan</label>
            <input className={`${inputCls} w-24`} type="number" placeholder="Urutan" value={f.sortOrder ?? 999} onChange={(e) => set("sortOrder", e.target.value)} />
          </div>

          <div className="rounded-xl border border-nm-border p-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold">Varian</h2>
              <button type="button" onClick={addV} className="text-xs font-semibold text-nm-red">+ Tambah varian</button>
            </div>
            <div className="mt-3 space-y-3">
              {f.variants.map((v, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 rounded-lg border border-nm-border p-2 sm:grid-cols-4">
                  <input className={inputCls} placeholder="Durasi" value={v.durationLabel} onChange={(e) => setV(i, "durationLabel", e.target.value)} required />
                  <input className={inputCls} placeholder="Kode (buy)" data-testid={`pf-code-${i}`} value={v.code} onChange={(e) => setV(i, "code", e.target.value)} required />
                  <input className={inputCls} type="number" placeholder="Harga" value={v.price} onChange={(e) => setV(i, "price", e.target.value)} required />
                  <input className={inputCls} type="number" placeholder="Stok" value={v.stock} onChange={(e) => setV(i, "stock", e.target.value)} />
                  <label className="col-span-2 flex items-center gap-2 text-xs sm:col-span-4">
                    <input type="checkbox" checked={v.isActive !== false} onChange={(e) => setV(i, "isActive", e.target.checked)} /> Varian aktif
                  </label>
                </div>
              ))}
            </div>
          </div>

          <button data-testid="pf-save" className={`${btn} w-full`} disabled={busy}>Simpan Produk</button>
        </form>
      </div>
    </div>
  );
}

export function AdminResetPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await call("/reset-password", { method: "POST", body: JSON.stringify({ token, new_password: pw }) });
      toast.success("Kata sandi direset. Silakan masuk.");
      nav("/admin");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell>
      <h1 className="text-xl font-extrabold text-nm-text">Reset Kata Sandi</h1>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <input className={inputCls} type="password" placeholder="Kata sandi baru (min 6)" value={pw} onChange={(e) => setPw(e.target.value)} required />
        <button className={`${btn} w-full`} disabled={busy || !token}>Simpan</button>
      </form>
    </Shell>
  );
}
